//controla estado (submenu aberto, collapsed)
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Nav,
  Navbar,
  Container,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { BsArrowReturnRight } from "react-icons/bs";

import "./Menu.css";

import { getMenuItemsByRole } from "./SidebarMenu/menu.config";
import { resolveRoleId } from "./SidebarMenu/menu.role";
import {
  isInAnySubmenu,
  isPathActive,
  openExternal,
} from "./SidebarMenu/menu.utils";
import { useAuth } from "../../context/AuthContext";

interface MenuProps {
  contentRef?: React.RefObject<HTMLElement | null>;
  collapsed?: boolean;
  onExpandSidebar?: () => void;
}

const Menu = ({ contentRef, collapsed = false, onExpandSidebar }: MenuProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement | null>(null);

  const { perfil } = useAuth();

  const roleId = useMemo(() => resolveRoleId(perfil), [perfil]);
  const menuItems = useMemo(() => getMenuItemsByRole(roleId), [roleId]);

  const [openMenuKey, setOpenMenuKey] = useState(null);

  const closeIfNotInSubmenu = useCallback(() => {
    if (!isInAnySubmenu(location.pathname, menuItems)) {
      setOpenMenuKey(null);
    }
  }, [location.pathname, menuItems]);

  useEffect(() => {
    closeIfNotInSubmenu();
  }, [closeIfNotInSubmenu]);

  useEffect(() => {
    if (collapsed) setOpenMenuKey(null);
  }, [collapsed]);

  const handleMenuClick = (item) => {
    const hasSubmenu = !!item.submenu?.length;

    if (collapsed && hasSubmenu) {
      if (typeof onExpandSidebar === "function") onExpandSidebar();
      setTimeout(() => setOpenMenuKey(item.key), 0);
      return;
    }

    if (item.external && item.url) {
      openExternal(item.url);
      setOpenMenuKey(null);
      return;
    }

    if (item.path) {
      navigate(item.path);
      setOpenMenuKey(null);
      return;
    }

    if (hasSubmenu) {
      setOpenMenuKey((prev) => (prev === item.key ? null : item.key));
    }
  };

  const handleSubClick = (path) => {
    navigate(path);
  };

  useEffect(() => {
    const onMouseDown = (event) => {
      const clickedInsideMenu = menuRef.current?.contains(event.target);
      const clickedInsideContent = contentRef?.current?.contains(event.target);

      if (!clickedInsideMenu && !clickedInsideContent) {
        if (!isInAnySubmenu(location.pathname, menuItems)) {
          setOpenMenuKey(null);
        }
      }
    };

    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [contentRef, location.pathname, menuItems]);

  return (
    <Navbar
      bg="light"
      expand="lg"
      className={`flex-column border-end vh-100 merit-menu ${
        collapsed ? "menu-collapsed" : ""
      }`}
      ref={menuRef}
    >
      <Container fluid>
        <Nav className="flex-column w-100">
          {menuItems.length > 0 ? (
            menuItems.map((item) => {
              const isActive = isPathActive(location.pathname, item);

              const linkContent = (
                <>
                  <span className="menu-icon">{item.icon}</span>
                  <span
                    className={`ms-1 text-primary menu-label ${isActive ? "fw-bold" : ""}`}
                  >
                    {item.label}
                  </span>
                </>
              );

              return (
                <div key={item.key} className="w-100">
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      collapsed ? (
                        <Tooltip id={`tt-${item.key}`}>{item.label}</Tooltip>
                      ) : (
                        <></>
                      )
                    }
                  >
                    <Nav.Link
                      className={`d-flex align-items-center w-100 text-start menu-item ${
                        isActive ? "active-menu" : ""
                      }`}
                      onClick={() => handleMenuClick(item)}
                    >
                      {linkContent}
                    </Nav.Link>
                  </OverlayTrigger>

                  {"submenu" in item && item.submenu && openMenuKey === item.key && !collapsed && (
                    <div className="submenu-container">
                      {item.submenu.map((sub: { key: string; label: string; path: string }) => {
                        const subActive = location.pathname.startsWith(
                          sub.path,
                        );

                        return (
                          <Nav.Link
                            key={sub.key}
                            className={`d-flex align-items-center w-100 text-start submenu-item ${
                              subActive ? "active-menu" : ""
                            }`}
                            onClick={() => handleSubClick(sub.path)}
                          >
                            <BsArrowReturnRight className="submenu-arrow" />
                            <span
                              className={`ms-3 text-primary submenu-label ${
                                subActive ? "fw-bold" : ""
                              }`}
                            >
                              {sub.label}
                            </span>
                          </Nav.Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div>Nenhum item de menu encontrado para este usuário.</div>
          )}
        </Nav>
      </Container>
    </Navbar>
  );
};

export default Menu;
